import React, { useEffect, useRef, useState } from 'react';
import SouthernLivingModal from '../modal/SouthernLivingModal';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

gsap.registerPlugin(ScrollTrigger);

const experienceCards = [
  {
    id: 1,
    color: 'yellow',
    title: 'A Home Designed for Hosting',
    description: 'With versatile propane, you can entertain with less effort and with greater reliability.',
    modal: {
      title: 'A Home Designed for Hosting',
      description: `Propane safely and seamlessly powers the essential elements of entertainment in the 2025 Southern Living Idea House, from the chef-worthy kitchen range and cozy indoor fireplace to the efficient tankless hot water heaters and premium outdoor grill. It’s the preferred energy choice due to its superior reliability and cost-effectiveness, especially when compared to natural gas or electric alternatives. With environmentally friendly propane, you can entertain with less effort and with greater peace of mind.`,
      mobileDescription:'Propane safely and seamlessly powers the elements of entertainment in the 2025 Southern Living Idea House, from the chef-worthy kitchen range and cozy fireplace to the efficient tankless water heaters and premium grill. It offers superior reliability and cost-effectiveness, especially when compared to natural gas or electric alternatives, helping you entertain with less effort and greater peace of mind.',
      images: [
        '/wp-content/themes/wlion-2021/assets/images/Southern_Living_card_1_modal_1.png',
        '/wp-content/themes/wlion-2021/assets/images/Southern_Living_card_1_modal_2.png',
        '/wp-content/themes/wlion-2021/assets/images/Southern_Living_card_1_modal_3.png',
        '/wp-content/themes/wlion-2021/assets/images/Southern_Living_card_1_modal_4.png',
      ],
      cta: {
        label: 'LEARN MORE',
        link: '/for-my-home',
      }
    },
    mobileImage: '/wp-content/themes/wlion-2021/assets/images/Southern_Living_card_1_mobile.png',
    desktopImage: '/wp-content/themes/wlion-2021/assets/images/Southern_Living_card_1_desktop.png',
  },
  {
    color: 'green',
    title: 'Comfort Around Every Corner',
    description:
      'With propane as your energy source, homes transform with ease into havens of comfort and relaxation.',
    modal: {
      title: 'Comfort Around Every Corner',
      description: `With propane as your energy source, homes transform into havens of comfort and relaxation with ease. In the 2025 Southern Living Idea House, propane powers everything from the sleek kitchen range and inviting fireplace, to the generator that provides peace of mind and the cutting-edge technology of the hydronic heating solution. Propane appliances use less energy, cost less to operate, and have fewer emissions compared with electric models — which is why designers, builders, and homeowners alike count on propane to bring home more comfort, savings, reliability, and versatility.`,
      mobileDescription:'With propane, homes transform into havens of comfort and relaxation. In the 2025 Southern Living Idea House, propane powers everything from the sleek kitchen range and inviting fireplace, to the always-ready generator and the cutting-edge technology of the hydronic heating system. Propane appliances use less energy, cost less to operate, and have fewer emissions compared with electric models — which is why designers, builders, and homeowners alike count on propane to bring home more comfort, savings, reliability, and versatility.',
      images: [
        '/wp-content/themes/wlion-2021/assets/images/Southern_Living_card_2_modal_1.png',
        '/wp-content/themes/wlion-2021/assets/images/Southern_Living_card_2_modal_2.png',
        '/wp-content/themes/wlion-2021/assets/images/Southern_Living_card_2_modal_3.png',
        '/wp-content/themes/wlion-2021/assets/images/Southern_Living_card_2_modal_4.png',
      ],
      cta: {
        label: 'LEARN MORE',
        link: '/for-my-home',
      }
    },
    mobileImage: '/wp-content/themes/wlion-2021/assets/images/Southern_Living_card_2_mobile.png',
    desktopImage: '/wp-content/themes/wlion-2021/assets/images/Southern_Living_card_2_desktop.png',
  },
  {
    color: 'teal',
    title: 'A Dependable, Clean Energy Solution',
    description:
      'Environmentally friendly propane empowers homeowners to turn their ideas into reality without compromising on peace of mind.',
    modal: {
      title: 'A Dependable, Clean Energy Solution',
      description: `As a safe and proven energy for this “modern homestead,” propane reliably powers all essential appliances. Not only does propane deliver the comfort and efficiency that make a house a home, but it also offers environmental advantages. A propane-powered generator provides dependable, affordable power when needed, while also being quieter, more efficient, and more environmentally friendly than diesel or gasoline options. Propane continues to set the standard for excellence, empowering homeowners to turn their ideas into reality without compromising on comfort or peace of mind.`,
      mobileDescription:'As a safe and proven energy for this “modern homestead,” propane reliably powers all the essential appliances. Not only does propane deliver on comfort and efficiency, but it also offers environmental advantages. A propane-powered generator provides dependable power when needed, while also reducing emissions compared to diesel or gasoline options. Propane empowers homeowners to turn their ideas into reality without compromising on comfort or peace of mind.',
      images: [
        '/wp-content/themes/wlion-2021/assets/images/Southern_Living_card_3_modal_1.png',
        '/wp-content/themes/wlion-2021/assets/images/Southern_Living_card_3_modal_2.png',
        '/wp-content/themes/wlion-2021/assets/images/Southern_Living_card_3_modal_3.png',
        '/wp-content/themes/wlion-2021/assets/images/Southern_Living_card_3_modal_4.png',
      ],
      cta: {
        label: 'LEARN MORE',
        link: '/for-my-home',
      }
    },
    mobileImage: '/wp-content/themes/wlion-2021/assets/images/Southern_Living_card_3_mobile.png',
    desktopImage: '/wp-content/themes/wlion-2021/assets/images/Southern_Living_card_3_desktop.png',
  },
];

gsap.registerPlugin(ScrollTrigger);

const SouthernLivingCards = () => {
  const el = useRef(null);
  const [activeCard, setActiveCard] = useState(null);

  const cardRefs = useRef([]);

  const handleOpenModal = (card) => setActiveCard(card);
  const handleCloseModal = () => setActiveCard(null);

useEffect(() => {
  if (window.innerWidth < 900) return;

  const wrapper = el.current.querySelector('.experience-section');
  if (!wrapper) return;

  gsap.set(wrapper, { paddingBottom: '600px' });

  const ctx = gsap.context(() => {
    const cards = cardRefs.current;
    const lastCard = cards[cards.length - 1];

    cards.forEach((card, index) => {
      if (!card) return;

      const offsetY = (index + 1) * 150;

      gsap.fromTo(
        card,
        { y: offsetY },
        {
          y: 0,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: card,
            start: 'top 90%',
            end: 'top 50%',
            scrub: 0.5,
            toggleActions: 'play reverse play reverse',
          },
        }
      );
    });

    if (lastCard) {
      gsap.to(wrapper, {
        paddingBottom: '64px',
        ease: 'none',
        scrollTrigger: {
          trigger: lastCard,
          start: 'top 90%',
          end: 'top 50%',
          scrub: true,
        },
      });
    }
  }, el);

  return () => ctx.revert();
}, []);

  return (
    <div ref={el}>
      <section className="experience-section">
        {experienceCards.map((card, index) => (
          <div
            key={index}
            className={`experience-card experience-card--${card.color}`}
            ref={(el) => (cardRefs.current[index] = el)}
          >
            <div className="experience-card__content">
              <h2 className="experience-card__title">{card.title}</h2>
              <p className="experience-card__description">{card.description}</p>
              <button className="experience-card__button" onClick={() => handleOpenModal(card)} title={`Click to learn more about ${card.title}`}>
                SEE HOW
              </button>
            </div>
            <div className="experience-card__images">
              <picture>
                <source
                  srcSet={card.desktopImage}
                  media="(min-width: 900px)"
                  type="image/png"
                />
                <img
                  src={card.mobileImage}
                  alt={card.title}
                  className="experience-card__image"
                  loading="lazy"
                  decoding="async"
                />
              </picture>
            </div>
          </div>
        ))}
      </section>
      <div className="experience-card__credit">Photography credit: Laurey W. Glenn/Southern Living</div>

      <SouthernLivingModal
        isOpen={!!activeCard}
        onClose={handleCloseModal}
        title={activeCard?.modal?.title}
        description={activeCard?.modal?.description}
        mobileDescription={activeCard?.modal?.mobileDescription}
        images={activeCard?.modal?.images}
        cta={activeCard?.modal?.cta}
        variant={activeCard?.color}
      />
    </div>
  );
};

export default SouthernLivingCards;
